import { ref } from 'vue'
import { api } from '@/global/services/api'
import { isArray } from '@/global/utils/utils'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import { TASKS_STREETS_ENDPOINT } from '@/modules/road-maintenance-module/winter-service-v2/tasks/config/constants'

/**
 * The routes of the marked work orders, with how far each order got along them.
 *
 * The same endpoint the tasks listing's map reads, asked a second question: given the
 * orders as well as their tasks, every street comes back carrying `coverage` - the
 * stretches those orders finished, started and never reached. The map draws them over the
 * street, each in the colour of how it went.
 *
 * Deliberately not the shared `useTaskStreetPaths` cache. That one remembers a task's
 * streets by the task alone, which is right for a screen showing where a route runs and
 * wrong here: what is drawn depends on which orders are marked, and two orders over the
 * same task are two different pictures.
 */
export function useWorkOrderStreetPaths () {
  const isFetching = ref(false)
  // Marks change faster than requests answer, and an earlier answer must not land on
  // top of a later one. Only the newest ask is allowed to.
  let latestRequestId = 0

  const normalizeIds = (ids) => [...new Set((isArray(ids) ? ids : [])
    .map(id => Number(id))
    .filter(id => !!id))]

  /**
   * The streets of the given tasks, told what the given orders covered. Answers with
   * the streets flattened, which is what the map panel takes, or with an empty list for
   * an ask that was already superseded by a later one.
   */
  const loadStreets = async (taskIds, execRouteIds) => {
    const tasks = normalizeIds(taskIds)

    if (!tasks.length) {
      return []
    }

    const requestId = ++latestRequestId
    isFetching.value = true

    try {
      const { data } = await api(WINTER_SERVICE_MODULE).post(TASKS_STREETS_ENDPOINT, {
        task_ids: tasks,
        exec_route_ids: normalizeIds(execRouteIds)
      })

      if (requestId !== latestRequestId) {
        return []
      }

      return isArray(data) ? data : []
    }
    catch (exception) {
      console.error(exception)

      return []
    }
    finally {
      // The loader belongs to the newest ask: an older one finishing must not take it
      // down while that one is still running.
      if (requestId === latestRequestId) {
        isFetching.value = false
      }
    }
  }

  /**
   * Marks were dropped rather than changed, so whatever is in flight is already stale.
   * Without this the answer to it would switch the loader back on after it was cleared.
   */
  const cancelPending = () => {
    latestRequestId += 1
    isFetching.value = false
  }

  return {
    isFetching,
    loadStreets,
    cancelPending
  }
}
